// Человек в ведомости: каскад удержаний по шагам и сводный экран по юрлицам. Сценарии 33 и 39.
//
// Панель открывается по нажатию на любую ячейку удержания, официальной части или итога — то
// есть на ячейку, которую человек в сетке править не может (правило 6). Она отвечает на
// единственный вопрос, который в этот момент есть: «почему к выплате именно столько».
//
// ## Каскад показан шагами, а не одной формулой
//
// Порядок здесь — не оформление, а суть: официальная часть вычитается ДО каскада, аванс
// удерживается первым, а если начисленного на аванс не хватило, прочие удержания НЕ
// проставляются вовсе и итог обнуляется. Свернув это в одну строку «начислено минус удержания»,
// мы теряем ровно то место, где деньги расходятся с ожиданием человека.
//
// Контроль заказчика, который обязан сходиться на этом экране: 240 000 плюс 30 000 минус
// 50 000 минус 12 764 минус 63 500 минус 18 500 равно 125 236.
//
// ## Итоги по юрлицам НЕ складываются
//
// Один человек в двух юридических лицах — это два независимых расчёта: разные ведомости, разные
// выплаты, разная касса. Одно общее число вместо двух читалось бы как «ему должны столько», а
// платят ему двумя разными путями.

import { useState } from 'react';

import { useLocation } from 'react-router';

import { RiCalendarLine, RiErrorWarningLine } from '@remixicon/react';

import type {
  MonthSiblingOut,
  PayrollColumnOut,
  PayrollEmployeeOut,
} from '@/shared/api/endpoints/payroll';
import { COLUMN_ADVANCE } from '@/shared/api/endpoints/payroll';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { can } from '@/shared/rbac/permissions';
import { buildSearch, parseSearch } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { ErrorState, Skeleton } from '@/shared/ui/screen-state';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import { ListRow } from '@/shared/ui/list-row';
import { КЕГЛЬ_ВЕЛИЧИНЫ } from '@/shared/ui/metric';
import { cn } from '@/utils/cn';

import { useEmployeeCard, useSetCells, useSetDeductionOverride } from '../queries';

const EDIT_PERMISSION = 'core.payroll.edit';
/**
 * Право отмечать выплату. Блок выплаты получает ЕГО, а не признак «ведомость правится»: сервер
 * ставит тот в ложь у утверждённой, выплаченной и закрытой ведомости, то есть именно там, где
 * выплаты и отмечают. До 01.10.2026 сюда передавалось `can_edit`, и финансовый отдел на
 * утверждённой ведомости получал на любое нажатие «отмечает финансовый отдел». В черновике
 * было наоборот: кнопка срабатывала у любого, а отказывал уже сервер.
 */
const MARK_FACT_PERMISSION = 'core.payout.mark_fact';

import { findEmployee, useSheetFromScreen } from './context';
import { FILTER_SHEET, PANEL_SHEET_PERSON } from '../../route';
import { ТЕКСТ_ПРИЧИНЫ, причинаПустоты } from '../причины';
import { авансВКолонке, перенесённыйАванс } from '../аванс';
import { АППАРАТ } from '../месяц';
import { PaidBlock, естьДоплата } from './PaidBlock';

export function PersonPanel({ id }: { id: string }) {
  const { grid, error, retry, nothing } = useSheetFromScreen();

  if (nothing !== null) {
    // Вечная загрузка запрещена: выключенный запрос навсегда остаётся «в ожидании», и панель
    // без этой развилки крутила бы скелет, ничего не собираясь показать.
    return <div className='text-paragraph-sm text-text-sub-600'>{nothing}</div>;
  }
  if (error !== null && error !== undefined) {
    return <ErrorState subject='расчёт по человеку' error={error as never} onRetry={retry} />;
  }
  if (grid === undefined) {
    return (
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-6 w-2/3 rounded-md' />
        <Skeleton className='h-56 w-full rounded-lg' />
      </div>
    );
  }

  const человек = findEmployee(grid, id);
  if (человек === null) {
    return (
      <div className='text-paragraph-sm text-text-sub-600'>
        Этого человека в открытой ведомости нет. Скорее всего, ссылка ведёт в другую ведомость:
        выберите её слева и откройте строку заново.
      </div>
    );
  }

  return (
    <Разбор
      человек={человек}
      колонки={grid.columns ?? []}
      рассчитан={человек.calculated_at !== null}
      период={{ year: grid.payroll.period_year, month: grid.payroll.period_month }}
      периодСловами={grid.payroll.period_title}
      подпись={`${grid.payroll.site_name ?? АППАРАТ} · ${grid.payroll.period_title}`}
      payrollId={grid.payroll.id}
      можноПравить={grid.payroll.can_edit}
      ведомостьВыплачена={grid.payroll.status === 'paid' || grid.payroll.status === 'closed'}
    />
  );
}

function Разбор({
  человек,
  колонки,
  рассчитан,
  период,
  периодСловами,
  подпись,
  payrollId,
  можноПравить,
  ведомостьВыплачена,
}: {
  человек: PayrollEmployeeOut;
  колонки: readonly PayrollColumnOut[];
  рассчитан: boolean;
  период: { year: number; month: number };
  /** «сентябрь 2026» с сервера. */
  периодСловами: string;
  подпись: string;
  payrollId: string;
  можноПравить: boolean;
  /** Ведомость выплачена целиком либо закрыта: блок выплаты доплату по ней не предлагает. */
  ведомостьВыплачена: boolean;
}) {
  const карточка = useEmployeeCard(человек.employee_id, период);
  const права = useAuthStore((state) => state.permissions);
  const официальная = (человек.official_paid ?? 0) + (человек.official_rest ?? 0);
  const прочие = прочиеУдержания(человек, колонки);
  const прочиеИтог = прочие.reduce((итог, пункт) => итог + пункт.amount, 0);
  // Шаги собраны из колонок ШАБЛОНА, а итог приходит с сервера. Удержание по колонке, которой в
  // шаблоне нет либо она скрыта, в перечень не попадёт — и четыре шага молча перестанут
  // сходиться с числом под ними. Это тот самый экран, который заказчик проверяет пальцем, и
  // расхождение здесь обязано быть НАЗВАНО, а не спрятано.
  // Неудержанный аванс прошлых месяцев лежит в колонке «Аванс», а не среди прочих: шаг 3
  // показывает его вместе с авансом месяца. Без него шаги расходились с итогом ровно на эту
  // сумму, и панель писала «не сходится» у каждого, кому его перенесли (13.10.2026).
  const перенесено = перенесённыйАванс(человек);
  const необъяснено = человек.after_advance - перенесено - прочиеИтог - человек.total_payout;
  const доплатить = рассчитан && естьДоплата(человек, ведомостьВыплачена);
  const выплата = рассчитан ? (
    <PaidBlock
      человек={человек}
      можноПравить={can(права, MARK_FACT_PERMISSION)}
      ведомостьВыплачена={ведомостьВыплачена}
    />
  ) : null;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-md text-text-strong-950'>{человек.employee_name}</span>
        <span className='text-paragraph-sm text-text-sub-600'>{подпись}</span>
      </div>

      {человек.review_notes.length > 0 && (
        <div className='flex flex-col gap-1 rounded-lg bg-bg-weak-50 p-3'>
          <span className='flex items-center gap-1 text-label-sm text-warning-base'>
            <RiErrorWarningLine className='size-4 shrink-0' />
            Требует разбора
          </span>
          <ul className='ml-4 list-disc text-paragraph-sm text-text-sub-600'>
            {человек.review_notes.map((пометка) => (
              <li key={пометка}>{пометка}</li>
            ))}
          </ul>
        </div>
      )}

      {!рассчитан ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          Расчёт по этому человеку ещё не проводился. Числа ниже — не нули, а неизвестные величины:
          показывать их нулями значило бы сказать, что к выплате ничего не положено.
        </p>
      ) : (
        <div className='flex flex-col gap-2'>
          <span className='text-label-sm text-text-strong-950'>Каскад по шагам</span>
          {(человек.explain ?? []).length > 0 && (
            // Объяснение, собранное СЕРВЕРОМ при пересчёте. Оно точнее собранного здесь: движок
            // знает и про удержания по колонкам, которых нет в шаблоне этого объекта.
            <div className='flex flex-col rounded-lg bg-bg-weak-50 px-4 py-2'>
              {(человек.explain ?? []).map((шаг) => (
                <div key={`${шаг.step}-${шаг.code}`} className='flex flex-col gap-0.5 py-1'>
                  <span className='flex items-baseline justify-between gap-2'>
                    <span className='text-paragraph-sm text-text-strong-950'>{шаг.title}</span>
                    {/* ⚠️ Знак берётся из направления, которое считает СЕРВЕР. Раньше шаги
                        печатались без знака прямо под блоком, где написано «Шаг 2. Минус
                        официальная часть»: сверху минус, снизу то же число без минуса, и
                        человек, разбирающий расчёт, читал это как два разных факта.

                        Вывести направление из кода шага нельзя: коды долговых шагов
                        собираются из вида долга на лету, и полного списка здесь не будет. */}
                    {шаг.amount != null && (
                      <span className='shrink-0 tabular-nums text-label-sm text-text-strong-950'>
                        {шаг.direction === 'deduction' && шаг.amount !== 0 ? '− ' : ''}
                        {formatKopecks(Math.abs(шаг.amount))}
                      </span>
                    )}
                  </span>
                  <span className='text-paragraph-xs text-text-sub-600'>{шаг.text}</span>
                </div>
              ))}
            </div>
          )}
          <div className='flex flex-col rounded-xl ring-1 ring-inset ring-stroke-soft-200'>
            <Шаг
              номер={1}
              подпись='Начислено'
              пояснение='по тарифным строкам и внетарифным начислениям'
              сумма={человек.accruals_total}
            />
            <Шаг
              номер={2}
              подпись='Минус официальная часть'
              пояснение={
                человек.no_official_row
                  ? 'человека нет в связанном официальном периоде: подставлены нули'
                  : `выплачено ${formatKopecks(человек.official_paid ?? 0)} плюс остаток ${formatKopecks(человек.official_rest ?? 0)}`
              }
              сумма={официальная}
              итог={человек.unofficial_base}
              итогПодпись='остаётся на карту до удержаний'
            />
            <Шаг
              номер={3}
              подпись='Минус аванс'
              пояснение={
                перенесено === 0
                  ? 'принятые заявки подотчёта с этим получателем; удерживаются первыми'
                  : человек.advance_total === 0
                    ? 'неудержанный аванс прошлых месяцев'
                    : `этого месяца ${formatKopecks(человек.advance_total)}, неудержанный прошлых месяцев ${formatKopecks(перенесено)}`
              }
              сумма={человек.advance_total + перенесено}
              итог={человек.after_advance - перенесено}
              итогПодпись={
                человек.after_advance < 0
                  ? 'начисленного на аванс не хватило'
                  : 'остаётся после аванса'
              }
              тревожно={человек.after_advance < 0}
            />
            {человек.flag_zeroed_by_cascade ? (
              <div className='flex flex-col gap-0.5 bg-bg-weak-50 px-4 py-2'>
                <span className='text-label-sm text-warning-base'>
                  Шаг 4: прочие удержания не проставлены
                </span>
                <span className='text-paragraph-xs text-text-sub-600'>
                  Не удержано: не хватило начисленного. Итог обнулён, остаток аванса{' '}
                  {formatKopecks(Math.abs(человек.after_advance))} перенесён в долг. Форма, билет,
                  обучение и удостоверение в этом месяце не удерживаются вовсе — иначе человек ушёл
                  бы в минус второй раз.
                </span>
              </div>
            ) : (
              <Шаг
                номер={4}
                подпись='Минус прочие удержания'
                пояснение={
                  прочие.length === 0
                    ? 'в этом расчёте ничего не удержано'
                    : прочие
                        .map((пункт) => `${пункт.title} ${formatKopecks(пункт.amount)}`)
                        .join(', ')
                }
                сумма={прочиеИтог}
              />
            )}
          </div>
          {необъяснено !== 0 && !человек.flag_zeroed_by_cascade && (
            <p className='rounded-lg bg-bg-weak-50 p-3 text-paragraph-sm text-warning-base'>
              Шаги не сходятся с итогом на {formatKopecks(Math.abs(необъяснено))}. Скорее всего,
              удержание прошло по колонке, которой нет в шаблоне этого объекта, — тогда его видно в
              ведомости другого шаблона. Покажите это финансовому отделу: считать по шагам, которые
              не сходятся, нельзя.
            </p>
          )}
          <div className='flex items-baseline justify-between rounded-lg bg-bg-weak-50 px-4 py-3'>
            <span className='text-label-sm text-text-strong-950'>
              Всего к выплате
              {человек.flag_zeroed_by_cascade && (
                <span className='pl-2 text-paragraph-xs font-normal text-warning-base'>
                  обнулено каскадом
                </span>
              )}
            </span>
            <span
              className={cn(
                КЕГЛЬ_ВЕЛИЧИНЫ.важный,
                'tabular-nums',
                человек.flag_zeroed_by_cascade ? 'text-warning-base' : 'text-text-strong-950',
              )}
            >
              {formatKopecks(человек.total_payout)}
            </span>
          </div>
          {человек.total_payout === 0 && !человек.flag_zeroed_by_cascade && (
            // Второй ноль: начисления равны официальной части. Это НЕ замечание, и говорить об
            // этом надо прямо — иначе человек пойдёт искать ошибку там, где всё правильно.
            //
            // Но ТОЛЬКО когда начислено было. До 23.09.2026 эта фраза стояла и у людей без
            // тарифа и без часов — под строкой «требует разбора: тариф не заполнен», то есть
            // панель спорила сама с собой. Тогда говорится настоящая причина.
            <p className='text-paragraph-xs text-text-sub-600'>
              {(() => {
                const настоящая = причинаПустоты(человек, null);
                if (настоящая !== null) {
                  return ТЕКСТ_ПРИЧИНЫ[настоящая];
                }
                return человек.accruals_total > 0
                  ? 'Ноль штатный: начисления равны официальной части, и на карту в этом месяце не идёт ничего. В разбор замечаний такая строка не попадает.'
                  : 'Начислено ноль, поэтому и на карту ноль: в этом месяце у человека нет ни часов, ни окладной суммы.';
              })()}
            </p>
          )}
        </div>
      )}

      {/* Сразу под каскадом, а не внизу панели: нули в шагах «аванс» и «прочие удержания» у
          человека на нескольких объектах объясняются именно здесь, и искать ответ ниже формы
          разового удержания значило бы не найти его вовсе. */}
      <ДругиеВедомостиМесяца человек={человек} колонки={колонки} периодСловами={периодСловами} />

      {/* Отметка выплаты стоит ПОСЛЕ каскада: сначала человек видит, сколько вышло, и только потом
          отмечает, сколько перевёл. Обратный порядок предлагал бы подтвердить сумму раньше, чем
          она посчитана. Кроме одного случая: есть что доплатить. Тогда это главное действие
          панели, и оно стоит выше редкой формы «Проставить сумму по колонке», а не под ней. */}
      {доплатить && выплата}

      <РазовоеУдержание
        человек={человек}
        колонки={колонки}
        payrollId={payrollId}
        можноПравить={можноПравить}
      />

      <ВводимыеКолонки
        человек={человек}
        колонки={колонки}
        payrollId={payrollId}
        можноПравить={можноПравить}
      />

      {!доплатить && выплата}

      <div className='flex flex-col gap-1'>
        <span className='text-label-sm text-text-strong-950'>Расчёты этого человека за месяц</span>
        {карточка.data === undefined ? (
          карточка.error === null ? (
            <Skeleton className='h-16 w-full rounded-lg' />
          ) : (
            <div className='flex flex-col items-start gap-2'>
              <p className='text-paragraph-sm text-text-sub-600'>
                Сводный экран по человеку сейчас не приехал. Разбор каскада выше от этого не
                зависит: он собран из той же сетки, что и ведомость.
              </p>
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='xxsmall'
                onClick={() => void карточка.refetch()}
              >
                Повторить
              </Button.Root>
            </div>
          )
        ) : (
          <>
            <FieldList>
              {(карточка.data.entities ?? []).map((юрлицо) => (
                <FieldRow
                  key={юрлицо.legal_entity_id}
                  label={юрлицо.legal_entity_name}
                  value={`${formatKopecks(юрлицо.total_payout)} к выплате`}
                  note={`начислено ${formatKopecks(юрлицо.accrued)} · ${countLabel(
                    юрлицо.payrolls.length,
                    ['ведомость', 'ведомости', 'ведомостей'],
                  )}`}
                />
              ))}
            </FieldList>
            {карточка.data.note !== null && (
              <p className='text-paragraph-xs text-text-sub-600'>{карточка.data.note}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Этот месяц человека в других его ведомостях того же юрлица (с 19.10.2026).
 *
 * Аванс, форма по графику и официальная часть берутся один раз на месяц и лежат в одной из его
 * ведомостей. В этой по ним ноль, и каскад выше говорит «аванс 0, ничего не удержано» — правду
 * про эту ведомость и пустоту про месяц. Блок отвечает на вопрос «а где тогда»: каждая другая
 * ведомость строкой, что в ней удержано и сколько к выплате, и сумма к выплате за месяц.
 *
 * Строка ведёт в ту ведомость сразу на этого человека: проверить пальцем, что форма там правда
 * стоит, — одно нажатие, а не поиск объекта в реестре и человека в сетке на 281 строку.
 *
 * Только ведомости, видимые смотрящему (их отбирает сервер), и официальная часть только тем, кому
 * она видна: у роли со «скрыто» её здесь нет, а не ноль. Нет соседей — нет и блока.
 */
function ДругиеВедомостиМесяца({
  человек,
  колонки,
  периодСловами,
}: {
  человек: PayrollEmployeeOut;
  колонки: readonly PayrollColumnOut[];
  периодСловами: string;
}) {
  const location = useLocation();
  const соседи = человек.month_siblings ?? [];
  if (соседи.length === 0) {
    return null;
  }
  const состояние = parseSearch(location.search);
  const адрес = (сосед: MonthSiblingOut) =>
    `${location.pathname}${buildSearch({
      ...состояние,
      filters: { ...состояние.filters, [FILTER_SHEET]: сосед.payroll_id },
      panels: [{ view: PANEL_SHEET_PERSON, id: сосед.payroll_employee_id }],
    })}`;
  // Итоги складываются честно: все ведомости месяца здесь одного юрлица (соседей сервер отбирает
  // по юрлицу), а разные юрлица не складываются никогда — см. шапку файла. Хоть одна ведомость
  // месяца не посчитана — итог месяца неизвестен, и вместо него прочерк, а не ноль (правило 7).
  const всеПосчитаны =
    человек.calculated_at !== null && соседи.every((сосед) => сосед.calculated_at !== null);
  const заМесяц = соседи.reduce((сумма, сосед) => сумма + сосед.total_payout, человек.total_payout);

  return (
    <Block
      icon={RiCalendarLine}
      title='Этот месяц в других ведомостях'
      // Коротко: подпись блока в одну строку, и длинная резалась бы на ширине панели.
      note={`${периодСловами} · к выплате, ₽`}
      flush
    >
      {соседи.map((сосед) => (
        <ListRow
          key={сосед.payroll_employee_id}
          to={адрес(сосед)}
          selected={false}
          blockInset
          wrapTitle
          // Хвост подписи — что именно удержано там: многоточие на ширине панели съедало бы его.
          wrapSubtitle
          title={сосед.site_name ?? АППАРАТ}
          subtitle={составСоседа(сосед, колонки)}
          value={сосед.calculated_at === null ? '—' : formatKopecks(сосед.total_payout)}
        />
      ))}
      <div className='flex items-baseline justify-between gap-3 px-5 py-2'>
        <span className='text-paragraph-sm text-text-sub-600'>
          {!всеПосчитаны
            ? 'Всего к выплате за месяц: не все ведомости посчитаны'
            : человек.month_siblings_hidden === true
              ? // Часть ведомостей месяца смотрящему не видна: сервер называет только сам факт,
                // без сумм, и итог честно говорит, по каким ведомостям он сложен.
                'Всего к выплате за месяц по ведомостям, которые вам видны'
              : 'Всего к выплате за месяц, с этой ведомостью'}
        </span>
        <span className='shrink-0 tabular-nums text-label-sm text-text-strong-950'>
          {всеПосчитаны ? formatKopecks(заМесяц) : '—'}
        </span>
      </div>
    </Block>
  );
}

/**
 * Что в соседней ведомости: состояние и всё, что в ней удержано, словами сетки.
 *
 * Названия удержаний — заголовки колонок ЭТОЙ ведомости: панель называет удержание теми же
 * словами, что и шапка рядом. Колонку, которой в этом шаблоне нет, называет её код: молча
 * пропустить удержание значило бы показать соседа беднее, чем он есть. Начислений соседа сервер
 * сюда не присылает: блок про то, что удержано там вместо здесь.
 */
function составСоседа(сосед: MonthSiblingOut, колонки: readonly PayrollColumnOut[]): string {
  if (сосед.calculated_at === null) {
    // Не посчитана — её суммы неизвестны, а не нулевые: «ничего не удержано» было бы неправдой.
    return `${сосед.status_title} · ещё не посчитана: суммы неизвестны`;
  }
  const части: string[] = [сосед.status_title];
  const аванс = авансВКолонке(сосед);
  if (аванс > 0) {
    части.push(`аванс ${formatKopecks(аванс)}`);
  }
  const официальная =
    сосед.official_paid === null || сосед.official_rest === null
      ? null
      : сосед.official_paid + сосед.official_rest;
  if (официальная !== null && официальная > 0) {
    части.push(`официальная часть ${formatKopecks(официальная)}`);
  }
  // В порядке колонок этой ведомости, как в шапке рядом; чего в её шаблоне нет — следом.
  const удержания = колонки.filter(
    (колонка) => колонка.kind === 'deduction' && колонка.code !== COLUMN_ADVANCE,
  );
  for (const колонка of удержания) {
    const сумма = сосед.amounts[колонка.code] ?? 0;
    if (сумма > 0) {
      части.push(`${колонка.title.toLowerCase()} ${formatKopecks(сумма)}`);
    }
  }
  const известные = new Set(удержания.map((колонка) => колонка.code));
  for (const [код, сумма] of Object.entries(сосед.amounts)) {
    if (код !== COLUMN_ADVANCE && !известные.has(код) && сумма > 0) {
      части.push(`${код} ${formatKopecks(сумма)}`);
    }
  }
  if (части.length === 1) {
    части.push('ничего не удержано');
  }
  return части.join(' · ');
}

/**
 * Прочие удержания человека: всё, что вид «удержание», кроме аванса.
 *
 * Аванс стоит отдельным шагом, потому что он удерживается ПЕРВЫМ и именно на нём каскад
 * обрывается; смешав его с формой и билетами, мы потеряли бы место, где итог обнуляется.
 * Заголовки берутся из колонок сервера: панель обязана называть удержание теми же словами, что
 * и шапка сетки.
 */
function прочиеУдержания(
  человек: PayrollEmployeeOut,
  колонки: readonly PayrollColumnOut[],
): { code: string; title: string; amount: number }[] {
  const суммы = человек.amounts ?? {};
  return колонки
    .filter((колонка) => колонка.kind === 'deduction' && колонка.code !== COLUMN_ADVANCE)
    .map((колонка) => ({
      code: колонка.code,
      title: колонка.title,
      amount: суммы[колонка.code] ?? 0,
    }))
    .filter((пункт) => пункт.amount !== 0);
}

/**
 * Один шаг каскада: что сделали, почему и сколько осталось.
 *
 * Сумма печатается БЕЗ знака, а направление действия называет подпись: «Шаг 2. Минус
 * официальная часть». До 25.08.2026 знак стоял в обоих местах сразу — «Минус прочие
 * удержания −1 500,00», — и это читалось как минус от минуса. Заметно это было только на
 * ненулевых шагах: нулевые печатались как «0,00» и выглядели правильно.
 */
function Шаг({
  номер,
  подпись,
  пояснение,
  сумма,
  итог,
  итогПодпись,
  тревожно,
}: {
  номер: number;
  подпись: string;
  пояснение: string;
  сумма: number;
  итог?: number;
  итогПодпись?: string;
  тревожно?: boolean;
}) {
  return (
    <div className='flex flex-col gap-0.5 border-b border-stroke-soft-200 px-4 py-2 last:border-b-0'>
      <div className='flex items-baseline justify-between gap-2'>
        <span className='text-paragraph-sm text-text-strong-950'>
          Шаг {номер}. {подпись}
        </span>
        <span className='shrink-0 tabular-nums text-label-sm text-text-strong-950'>
          {formatKopecks(сумма)}
        </span>
      </div>
      <span className='text-paragraph-xs text-text-sub-600'>{пояснение}</span>
      {/* Остаток после шага показывается, только если шаг его ИЗМЕНИЛ.
          На живом экране цепочка выглядела так: «Шаг 1. Начислено 47 419,35», «Шаг 2. Минус
          официальная часть 0,00 — остаётся 47 419,35», «Шаг 3. Минус аванс 0,00 — остаётся
          47 419,35». Одно и то же число пять раз подряд, из них три раза — после шагов,
          которые ничего не вычли. Человек читает это как разные величины и ищет между ними
          разницу, которой нет. */}
      {итог !== undefined && сумма !== 0 && (
        <div className='flex items-baseline justify-between gap-2'>
          <span className='text-paragraph-xs text-text-sub-600'>{итогПодпись}</span>
          <span
            className={cn(
              'shrink-0 tabular-nums text-paragraph-xs',
              тревожно === true ? 'text-warning-base' : 'text-text-sub-600',
            )}
          >
            {formatKopecks(итог)}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Ввод по колонкам, которые вводятся с клавиатуры, — включая те, что требуют основания.
 *
 * В сетке такие колонки правятся прямо в ячейке, а сюда вынесены те, у кого без основания
 * сумма не сохранится: основание в ячейку не помещается, и отказ сервера прилетел бы уже после
 * того, как человек решил, что ввод принят. Штраф у заказчика именно такой — и он, к слову,
 * колонкой почти не пользуется: взыскание он проводит понижением тарифа через целевую сумму.
 */
/**
 * Виды удержаний по долгам, которым финотдел ставит разовую сумму на одну ведомость (тз 5.5).
 * Тот же состав, что и на сервере: аванс удерживается своим шагом, штраф вводится руками.
 */
const ВИДЫ_ПО_ДОЛГАМ = new Set(['tickets', 'training', 'uniform', 'license', 'credit', 'housing']);

/** Основание возврата к графику: решение само по себе понятное, человека не спрашиваем. */
const ВЕРНУТЬ_ПО_ГРАФИКУ = 'вернули удержание по графику';

/**
 * Разовое удержание по долгу в этой ведомости вместо графика.
 *
 * 12.10.2026 заказчик: «в сентябре у Саввушкина удержали обучение 2 000 рублей… Дальше должен
 * оставаться обычный график». Раньше для этого меняли график долга, а он живёт дальше месяца:
 * забытый возврат удержал бы с человека лишнее в октябре. Здесь сумма действует ровно в этой
 * ведомости, следующий месяц идёт по графику сам.
 */
function РазовоеУдержание({
  человек,
  колонки,
  payrollId,
  можноПравить,
}: {
  человек: PayrollEmployeeOut;
  колонки: readonly PayrollColumnOut[];
  payrollId: string;
  можноПравить: boolean;
}) {
  const права = useAuthStore((state) => state.permissions);
  const запись = useSetDeductionOverride(payrollId);
  const виды = колонки.filter(
    (колонка) => колонка.kind === 'deduction' && ВИДЫ_ПО_ДОЛГАМ.has(колонка.code),
  );
  const [вид, setВид] = useState<string>(NO_VALUE);
  const [сумма, setСумма] = useState('');
  const [основание, setОснование] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [сообщение, setСообщение] = useState<string | null>(null);

  if (виды.length === 0) {
    return null;
  }
  const разово = человек.deduction_overrides ?? {};
  const суммы = человек.amounts ?? {};
  const название = (код: string) => виды.find((пункт) => пункт.code === код)?.title ?? код;

  function можно(): boolean {
    if (!can(права, EDIT_PERMISSION)) {
      setЗапрет('Удержания в ведомости меняет финансовый отдел или администратор.');
      return false;
    }
    if (!можноПравить) {
      setЗапрет('Эта ведомость закрыта для правок.');
      return false;
    }
    return true;
  }

  function записать(код: string, amount: number | null, basis: string) {
    setСообщение(null);
    setЗапрет(null);
    запись.mutate(
      {
        employeeId: человек.employee_id,
        columnCode: код,
        body: { amount, basis, version: человек.version },
      },
      {
        onSuccess: (обновлённый) => {
          setСумма('');
          setОснование('');
          setСообщение(
            (amount === null
              ? `«${название(код)}» снова удерживается по графику.`
              : `«${название(код)}» в этой ведомости: ${formatKopecks(amount)}.`) +
              ` Итог к выплате стал ${formatKopecks(обновлённый.total_payout)}.`,
          );
        },
      },
    );
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-3'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-sm text-text-strong-950'>
          Удержать по долгу другую сумму в этой ведомости
        </span>
        <span className='text-paragraph-xs text-text-sub-600'>
          Только в этом месяце: со следующего удержание снова пойдёт по графику долга. Ноль — в этом
          месяце не удерживать.
        </span>
      </div>

      {Object.entries(разово).map(([код, решение]) => (
        <div
          key={код}
          className='flex items-start justify-between gap-2 rounded-lg bg-bg-weak-50 px-3 py-2'
        >
          <span className='flex min-w-0 flex-col gap-0.5'>
            <span className='text-paragraph-sm text-text-strong-950'>
              {название(код)}: разово {formatKopecks(решение.amount)}
            </span>
            <span data-текст='от человека' className='text-paragraph-xs text-text-sub-600'>
              {решение.basis}
            </span>
          </span>
          <Button.Root
            variant='neutral'
            mode='stroke'
            size='xxsmall'
            className='shrink-0'
            onClick={() => можно() && записать(код, null, ВЕРНУТЬ_ПО_ГРАФИКУ)}
          >
            Вернуть по графику
          </Button.Root>
        </div>
      ))}

      <SelectField
        id='sheet-person-debt-kind'
        label='Удержание'
        value={вид}
        options={виды.map((пункт) => ({
          value: пункт.code,
          label: `${пункт.title} · сейчас ${formatKopecks(суммы[пункт.code] ?? 0)}`,
        }))}
        onChange={setВид}
        placeholder='выберите удержание'
      />
      <TextField
        id='sheet-person-debt-amount'
        label='Сумма в этой ведомости'
        hint='рублями: 2 000. Ноль — не удерживать в этом месяце'
        value={сумма}
        onChange={setСумма}
      />
      <TextField
        id='sheet-person-debt-basis'
        label='Основание'
        hint='обязательно, например: удержано при выплате 11.10'
        value={основание}
        onChange={setОснование}
      />

      <Button.Root
        variant='neutral'
        mode='stroke'
        size='small'
        onClick={() => {
          setСообщение(null);
          if (!можно()) {
            return;
          }
          if (вид === NO_VALUE) {
            setЗапрет('Выберите удержание: без него непонятно, какой долг гасить.');
            return;
          }
          if (сумма.trim() === '') {
            setЗапрет('Поставьте сумму. Ноль, если в этом месяце не удерживать.');
            return;
          }
          const разбор = parseRublesToKopecks(сумма);
          if (!разбор.ok) {
            setЗапрет(moneyErrorText(разбор.reason, `сумму «${название(вид)}»`));
            return;
          }
          if (основание.trim().length < 3) {
            setЗапрет('Напишите основание: через месяц никто не вспомнит, почему удержали иначе.');
            return;
          }
          записать(вид, разбор.value, основание.trim());
        }}
      >
        {запись.isPending ? 'Записываем…' : 'Удержать в этой ведомости'}
      </Button.Root>

      {запрет !== null && <p className='text-paragraph-xs text-text-sub-600'>{запрет}</p>}
      {сообщение !== null && <p className='text-paragraph-sm text-text-sub-600'>{сообщение}</p>}
      {запись.error !== null && <ErrorAlert error={запись.error as never} />}
    </div>
  );
}

function ВводимыеКолонки({
  человек,
  колонки,
  payrollId,
  можноПравить,
}: {
  человек: PayrollEmployeeOut;
  колонки: readonly PayrollColumnOut[];
  payrollId: string;
  можноПравить: boolean;
}) {
  const права = useAuthStore((state) => state.permissions);
  const ячейки = useSetCells(payrollId);
  const вводимые = колонки.filter((колонка) => колонка.behaviour === 'input');
  const [колонка, setКолонка] = useState<string>(NO_VALUE);
  const [сумма, setСумма] = useState('');
  const [основание, setОснование] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [сообщение, setСообщение] = useState<string | null>(null);

  if (вводимые.length === 0) {
    return null;
  }
  const выбранная = вводимые.find((пункт) => пункт.code === колонка) ?? null;

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-3'>
      <div className='flex flex-col gap-0.5'>
        <span className='text-label-sm text-text-strong-950'>Проставить сумму по колонке</span>
        <span className='text-paragraph-xs text-text-sub-600'>
          Премии и прочие вводимые колонки правятся прямо в сетке. Здесь — то же самое плюс поле
          основания для колонок, которые его требуют.
        </span>
      </div>

      <SelectField
        id='sheet-person-column'
        label='Колонка'
        value={колонка}
        options={вводимые.map((пункт) => ({ value: пункт.code, label: пункт.title }))}
        onChange={setКолонка}
        placeholder='выберите колонку'
      />
      <TextField
        id='sheet-person-amount'
        label='Сумма'
        hint='рублями: 5 000 либо 5000,50. Пусто — убрать сумму'
        value={сумма}
        onChange={setСумма}
      />
      {выбранная?.requires_basis === true && (
        <TextField
          id='sheet-person-basis'
          label='Основание'
          hint='обязательно для этой колонки'
          value={основание}
          onChange={setОснование}
        />
      )}

      <Button.Root
        variant='neutral'
        mode='stroke'
        size='small'
        onClick={() => {
          setСообщение(null);
          if (!can(права, EDIT_PERMISSION)) {
            setЗапрет('Править ведомость может финансовый отдел или администратор.');
            return;
          }
          if (!можноПравить) {
            setЗапрет('Эта ведомость закрыта для правок.');
            return;
          }
          if (выбранная === null) {
            setЗапрет('Выберите колонку: без неё непонятно, куда класть сумму.');
            return;
          }
          const разбор =
            сумма.trim() === '' ? { ok: true as const, value: 0 } : parseRublesToKopecks(сумма);
          if (!разбор.ok) {
            setЗапрет(moneyErrorText(разбор.reason, `сумму по колонке «${выбранная.title}»`));
            return;
          }
          if (выбранная.requires_basis && основание.trim() === '') {
            setЗапрет(
              `Колонка «${выбранная.title}» требует основания: без него сумма не сохранится.`,
            );
            return;
          }
          setЗапрет(null);
          ячейки.mutate(
            {
              employeeId: человек.employee_id,
              body: {
                cells: [
                  {
                    column_code: выбранная.code,
                    amount: разбор.value,
                    basis: основание.trim() === '' ? null : основание.trim(),
                  },
                ],
                version: человек.version,
              },
            },
            {
              onSuccess: (обновлённый) => {
                setОснование('');
                setСообщение(
                  `Записано. Итог к выплате стал ${formatKopecks(обновлённый.total_payout)}.`,
                );
              },
            },
          );
        }}
      >
        {ячейки.isPending ? 'Записываем…' : 'Записать и пересчитать человека'}
      </Button.Root>

      {запрет !== null && <p className='text-paragraph-xs text-text-sub-600'>{запрет}</p>}
      {сообщение !== null && <p className='text-paragraph-sm text-text-sub-600'>{сообщение}</p>}
      {ячейки.error !== null && <ErrorAlert error={ячейки.error as never} />}
    </div>
  );
}
